// Cầu nối giữa form sign_zones và iframe pdf.js (pdfjs-annotation-extension).
// Component này không biết "biên bản" là gì — nó chỉ dịch sign_zones <-> annotation.
import "./AgreementSignZone.scss";

import {
  SIGN_ZONE_ACTION,
  SIGN_ZONE_COLOR,
} from "constants/AgreementSignZoneConstant";
import {
  applyAnnotationChange,
  buildViewerUrl,
  initAnnotation,
} from "pages/IssueInvoice/helper/agreementSignZone";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";

// Bao lâu không thấy "documentloaded" thì coi như iframe hỏng. Đo thật trên
// UAT: nạp đủ mất dưới 2s; để 20s cho máy chậm và mạng kém.
const VIEWER_LOAD_TIMEOUT_MS = 20000;

// Nhịp dò để phát lại sự kiện bị lỡ. Đủ thưa để không tốn gì, đủ dày để người
// dùng không kịp thấy nút bị khoá.
const VIEWER_REPAIR_INTERVAL_MS = 400;

const AgreementSignZoneViewer = props => {
  const {
    mainForm,
    filePdfUrl,
    userName,
    handleActionDocument,
    style,
    reloadNonce,
    onLoadTimeout,
  } = props;

  const iframeRef = useRef(null);
  const { getValues, setValue, watch } = mainForm;

  const pdfJsLoaded = watch("pdfJsLoaded");

  const viewerUrl = useMemo(
    () =>
      filePdfUrl ? buildViewerUrl(filePdfUrl, userName, reloadNonce) : "",
    [filePdfUrl, userName, reloadNonce]
  );

  const postToViewer = message => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }
    iframeRef.current.contentWindow.postMessage(message, "*");
  };

  // Nhận sự kiện từ iframe pdf.js
  useEffect(() => {
    const handleMessage = event => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const { type, annotationChange } = event.data || {};

      if (type === "documentloaded") {
        setValue("pdfJsLoaded", true);
        return;
      }

      if (type !== "annotationChange") {
        return;
      }

      // Chỉ quan tâm khung ký, bỏ qua mọi annotation khác
      if (annotationChange?.rawAnnotationStore?.subtype !== "Square") {
        return;
      }

      const result = applyAnnotationChange(
        getValues("sign_zones"),
        annotationChange
      );

      if (!result) {
        console.warn(
          "AgreementSignZoneViewer: không tìm thấy vùng ký khớp annotation",
          annotationChange?.id
        );
        return;
      }

      setValue(result.path, result.value, { shouldValidate: true });
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Đổi file (hoặc component mount lại) là pdf.js nạp document mới từ đầu.
  // Phải hạ cờ xuống trước, nếu không cờ còn "true" của document cũ sẽ khiến
  // effect vẽ lại vùng ký bên dưới không chạy cho document mới.
  useEffect(() => {
    setValue("pdfJsLoaded", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerUrl]);

  // Canh giờ nạp iframe.
  //
  // Viewer chỉ báo "documentloaded" qua pdfjs-annotation-extension. Bundle đó
  // nặng ~3MB và là ES module: về thiếu byte là trình duyệt bỏ luôn, KHÔNG có
  // sự kiện error nào trên thẻ script cha. Đã gặp thật trên UAT 2026-09-09,
  // nginx chỉ gửi 2.772.992/3.113.640 byte. Hậu quả cũ: PDF vẫn hiện bình
  // thường nhưng nút "Xác nhận ký số" khoá vĩnh viễn, không lời giải thích.
  //
  // Nên phải tự canh giờ; onload của iframe không dùng được vì nó bắn ngay cả
  // khi module bên trong chết.
  //
  // Canh giờ này còn gánh thêm một hỏng hóc thứ hai, dạng chập chờn: extension
  // nạp đủ và khởi tạo xong nhưng BỎ LỠ sự kiện "documentloaded" của pdf.js.
  // Nó gắn bộ lắng nghe trong lúc bundle 3MB còn đang phân tích, mà tài liệu ở
  // đây là blob URL nên pdf.js mở xong gần như tức thì. Ai xong trước là tuỳ
  // máy và tuỳ lần tải, nên lúc chạy lúc không.
  //
  // Triệu chứng giống hệt lần nạp hụt: PDF hiện đẹp, nút khoá vĩnh viễn. Phân
  // biệt bằng window.Konva trong iframe — nạp hụt thì rỗng, lỡ sự kiện thì có.
  //
  // Sửa bằng cách phát lại đúng sự kiện đó từ trang cha. Iframe cùng origin nên
  // với tới eventBus được. Phát lại chạy đúng nhánh khởi tạo của extension chứ
  // không phải bật cờ suông, nên các vùng ký cũng được dựng đúng.
  useEffect(() => {
    if (!viewerUrl || pdfJsLoaded) {
      return undefined;
    }

    let repaired = false;

    // Chỉ phát lại KHI extension đã sẵn sàng mà vẫn im. Phát sớm hơn thì nó
    // chưa gắn bộ lắng nghe, mất công vô ích.
    const repairTimer = setInterval(() => {
      if (repaired) {
        return;
      }

      const win = iframeRef.current?.contentWindow;
      const app = win?.PDFViewerApplication;

      // window.Konva do chính bundle extension đặt: có nghĩa là nó đã chạy.
      if (!app?.pdfDocument || !win?.Konva) {
        return;
      }

      repaired = true;
      try {
        app.eventBus.dispatch('documentloaded', { source: app });
      } catch (error) {
        console.error('AgreementSignZoneViewer: phát lại documentloaded lỗi', error);
      }
    }, VIEWER_REPAIR_INTERVAL_MS);

    const timer = setTimeout(() => {
      if (onLoadTimeout) {
        onLoadTimeout();
      }
    }, VIEWER_LOAD_TIMEOUT_MS);

    return () => {
      clearInterval(repairTimer);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerUrl, pdfJsLoaded]);

  // PDF nạp xong thì vẽ lại các vùng ký đã lưu
  useEffect(() => {
    if (!pdfJsLoaded) {
      return;
    }

    const annotations = [];

    (getValues("sign_zones") || []).forEach(signZone => {
      (signZone.zone_infos || []).forEach(zoneInfo => {
        // Vùng ký chưa được vẽ lên PDF thì không có gì để hiển thị
        if (!zoneInfo.zone_width || !zoneInfo.zone_height) {
          return;
        }

        annotations.push(
          initAnnotation({
            id: zoneInfo.zone_id,
            x: Number(zoneInfo.zone_x),
            y: Number(zoneInfo.zone_y),
            width: Number(zoneInfo.zone_width),
            height: Number(zoneInfo.zone_height),
            pageNumber: Number(zoneInfo.zone_page) + 1,
            stroke: SIGN_ZONE_COLOR[signZone.node_code],
            contentsObjText: `Vùng ký của ${signZone.node_name}`,
            // Khoá vùng ký: chỉ được xem, không kéo và không co giãn
            readonly: true,
          })
        );
      });
    });

    if (annotations.length > 0) {
      postToViewer({ type: "loadAnnotations", annotations });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfJsLoaded]);

  // Thực thi lệnh do panel phát ra
  useEffect(() => {
    const { action, field, content, format } = handleActionDocument || {};

    if (!action || !field) {
      return;
    }

    const annotationData = {
      id: field,
      // Vùng ký bị khoá nên toạ độ đặt sẵn là toạ độ cuối cùng
      x: Number(format?.x) || 5,
      y: Number(format?.y) || 5,
      width: Number(format?.width) || 170,
      height: Number(format?.height) || 50,
      // format.page lưu 0-based như zone_page, pdf.js đánh số từ 1.
      // Biên bản điều chỉnh thông tin đặt vùng ký ở trang 2.
      pageNumber: (Number(format?.page) || 0) + 1,
      stroke: format?.stroke || SIGN_ZONE_COLOR.PARTY_A,
      contentsObjText: content || "",
      // Khoá vùng ký: chỉ được xem, không kéo và không co giãn
      readonly: true,
    }
    console.log("AgreementSignZoneViewer action change", {
      action,
      field,
      content,
      format,
      annotationData
    });
    switch (action) {
      case SIGN_ZONE_ACTION.CREATE:
        // Nút "Thêm vùng ký" (không có trang cố định): addAnnotations tự gán
        // khung vào trang đang xem, cuộn tới và báo annotationChange về form.
        if (format?.page === undefined || format?.page === null) {
          postToViewer({
            type: "addAnnotations",
            annotations: [initAnnotation(annotationData)],
          });
          break;
        }

        // Khung đặt sẵn có trang cố định (vd biên bản INFO ký ở trang 2):
        // addAnnotations BỎ QUA pageNumber, luôn lấy trang đang xem. Mà PDF ngắn
        // (2 trang lọt hết khung nhìn) thì pdf.js không cuộn được sang trang 2
        // -> trang đang xem luôn là 1, chuyển trang hay chờ setTimeout đều vô
        // ích. loadAnnotations giữ nguyên pageNumber và cũng gọi updatePdfjs để
        // pdf.js vẽ lại trang (khung chỉ hiện khi trang render lại).
        // Form không cần annotationChange: panel đã ghi sẵn trang/toạ độ/kích
        // thước lúc thêm, và vùng ký bị khoá nên toạ độ không đổi.
        postToViewer({
          type: "loadAnnotations",
          annotations: [initAnnotation(annotationData)],
        });
        // Cuộn tới khung vừa đặt. Message là macrotask nên chạy sau khi lệnh
        // trên đã lưu khung vào store của extension.
        postToViewer({ type: "selectAnnotation", annotationId: field });
        break;

      case SIGN_ZONE_ACTION.VIEW:
        postToViewer({ type: "selectAnnotation", annotationId: field });
        break;

      case SIGN_ZONE_ACTION.DELETE:
        postToViewer({ type: "deleteAnnotation", annotationId: field });
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleActionDocument]);

  return (
    <div className="agreement-sign-zone-viewer" style={style}>
      {viewerUrl ? (
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          title="Vùng ký biên bản"
          className="agreement-sign-zone-viewer__iframe"
          allowFullScreen
        />
      ) : (
        <div className="agreement-sign-zone-viewer__placeholder">
          Không có file PDF để hiển thị
        </div>
      )}
    </div>
  );
};

AgreementSignZoneViewer.propTypes = {
  mainForm: PropTypes.object.isRequired,
  filePdfUrl: PropTypes.string,
  userName: PropTypes.string,
  handleActionDocument: PropTypes.object,
  style: PropTypes.object,
  reloadNonce: PropTypes.number,
  onLoadTimeout: PropTypes.func,
};

AgreementSignZoneViewer.defaultProps = {
  filePdfUrl: "",
  userName: "",
  handleActionDocument: null,
  style: {},
  reloadNonce: 0,
  onLoadTimeout: null,
};

export default AgreementSignZoneViewer;
